import api from './api';
import { API_ENDPOINTS } from '../utils/constants';
import { dedupeInflight } from '../utils/dedupedFetch';

function minFromPriceFromRooms(rooms) {
  let fromPrice = null;
  for (const r of rooms || []) {
    const dp = r?.price?.discountedPrice;
    if (dp == null || !Number.isFinite(Number(dp)) || dp <= 0) continue;
    fromPrice = fromPrice == null ? Number(dp) : Math.min(fromPrice, Number(dp));
  }
  return fromPrice;
}

function mapStayToPawStoryRow(stay) {
  const url =
    stay?.pawStoryVideoUrl && String(stay.pawStoryVideoUrl).trim();
  if (!url) return null;
  return {
    stayId: stay.stayId,
    title: stay.title,
    city: stay.city,
    state: stay.state,
    pawStoryVideoUrl: url,
    coverImage: Array.isArray(stay.images) && stay.images[0] ? stay.images[0] : null,
    fromPrice: minFromPriceFromRooms(stay.rooms)
  };
}

/**
 * Stays API Service
 * Handles all stays-related API calls
 */
export const staysApi = {
  /**
   * Get all stays with optional pagination
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of stays to return
   * @param {number} params.offset - Offset for pagination
   * @returns {Promise<Object>} Stays data with pagination
   */
  getAllStays: async (params = {}) => {
    try {
      const response = await api.get(API_ENDPOINTS.STAYS, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Cities that have at least one stay (sorted)
   * @returns {Promise<string[]>}
   */
  getStayCities: () =>
    dedupeInflight(
      'stays:cities',
      async () => {
        const response = await api.get(API_ENDPOINTS.STAYS_CITIES);
        return response.data.cities || [];
      },
      { cacheMs: 120000 }
    ),

  /**
   * Get stays in spotlight (inSpotlight: true)
   * @returns {Promise<Array>} Array of spotlight stays
   */
  /**
   * Stays that have pawStoryVideoUrl set (Paw Stories reels on home / stays / events).
   * @returns {Promise<Array>} Slim stay objects with stayId, title, city, state, pawStoryVideoUrl, coverImage, fromPrice
   */
  getPawStoryStays: function getPawStoryStays() {
    return dedupeInflight(
      'stays:paw-stories',
      async () => {
        try {
          const response = await api.get(API_ENDPOINTS.STAYS_PAW_STORIES);
          return response.data.stays || [];
        } catch (error) {
          if (error.response?.status !== 404) throw error;

          try {
            const { stays } = await staysApi.searchStaysAll({ pawStories: true });
            if (Array.isArray(stays) && stays.length > 0) return stays;
          } catch (fallbackErr) {
            console.warn('Paw stories search fallback failed:', fallbackErr?.message || fallbackErr);
          }

          const { stays: all } = await staysApi.searchStaysAll({});
          return (all || []).map(mapStayToPawStoryRow).filter(Boolean);
        }
      },
      { cacheMs: 120000 }
    );
  },

  getSpotlightStays: () =>
    dedupeInflight(
      'stays:spotlight',
      async () => {
        try {
          const response = await api.get(API_ENDPOINTS.STAYS_SPOTLIGHT);
          return response.data.stays || [];
        } catch (error) {
          const limit = 100;
          let offset = 0;
          const spotlightStays = [];
          let hasMore = true;
          while (hasMore) {
            const r = await api.get(API_ENDPOINTS.STAYS, { params: { limit, offset } });
            const stays = r.data.stays || [];
            spotlightStays.push(...stays.filter((stay) => stay.inSpotlight === true));
            hasMore = r.data.pagination?.hasMore === true;
            offset += limit;
            if (offset > 50000) break;
          }
          return spotlightStays;
        }
      },
      { cacheMs: 120000 }
    ),

  /**
   * Search stays by city/state with filters
   * @param {Object} params - Search parameters
   * @param {string} params.city - City name
   * @param {string} params.state - State name
   * @param {string} params.checkIn - Check-in date (YYYY-MM-DD)
   * @param {string} params.checkOut - Check-out date (YYYY-MM-DD)
   * @param {number} params.guests - Number of guests
   * @param {number} params.minRating - Minimum rating filter
   * @param {number} params.maxPrice - Maximum price filter
   * @param {Array} params.features - Array of feature names
   * @returns {Promise<Array>} Filtered stays
   */
  searchStays: async (params = {}) => {
    try {
      const response = await api.get(API_ENDPOINTS.STAYS_SEARCH, { params });
      return response.data; // Return the full response data, not just stays
    } catch (error) {
      throw error;
    }
  },

  /** Paginate search until all matching stays are loaded. */
  searchStaysAll: async function searchStaysAll(params = {}) {
    const pageSize = 100;
    let offset = 0;
    const allStays = [];
    let lastPagination = null;

    for (;;) {
      const data = await this.searchStays({
        ...params,
        limit: pageSize,
        offset
      });
      const page = data.stays || [];
      allStays.push(...page);
      lastPagination = data.pagination;
      if (!data.pagination?.hasMore || page.length === 0) break;
      offset += pageSize;
    }

    return {
      stays: allStays,
      pagination: {
        ...(lastPagination || {}),
        total: allStays.length,
        limit: pageSize,
        offset: 0,
        hasMore: false
      }
    };
  },

  /**
   * Get stay by ID
   * @param {string} stayId - Stay ID
   * @returns {Promise<Object>} Stay data
   */
  getStayById: (stayId) => {
    if (!stayId) {
      return Promise.reject(new Error('stayId is required'));
    }
    return dedupeInflight(
      `stays:by-id:${stayId}`,
      async () => {
        const response = await api.get(API_ENDPOINTS.STAYS_BY_ID(stayId));
        return response.data;
      },
      { cacheMs: 30000 }
    );
  },

  /**
   * Get stays by city
   * @param {string} city - City name
   * @returns {Promise<Array>} Stays in the specified city
   */
  getStaysByCity: async (city) => {
    try {
      const response = await api.get(API_ENDPOINTS.STAYS_SEARCH, {
        params: { city, state: '' } // Empty state to get all stays in city
      });
      return response.data.stays || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get stays by state
   * @param {string} state - State name
   * @returns {Promise<Array>} Stays in the specified state
   */
  getStaysByState: async (state) => {
    try {
      const response = await api.get(API_ENDPOINTS.STAYS_SEARCH, {
        params: { city: '', state } // Empty city to get all stays in state
      });
      return response.data.stays || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get stays with high ratings
   * @param {number} minRating - Minimum rating threshold
   * @returns {Promise<Array>} High-rated stays
   */
  getHighRatedStays: async (minRating = 4.0) => {
    try {
      const response = await api.get(API_ENDPOINTS.STAYS_SEARCH, {
        params: { minRating }
      });
      return response.data.stays || [];
    } catch (error) {
      throw error;
    }
  },
};

export default staysApi; 